import { getUnauthenticatedBaseUrl } from '../network/getUploadConfig';
import { contentTypeJson } from '../network/constants';
import { fetchRetry } from '../network/fetchRetry';
import { getLogger } from '../logger';

const log = getLogger('getScaToken');
let cachedPromise: Promise<string> | undefined;
let cachedToken: string | undefined = undefined;

/**
 * Retrieves a SCA token for making non-user-authenticated requests to the collector (when not using
 * SSO tokens). Stores this in a page-level cache for the duration of the page session.
 *
 * @param ignoreCache Don't read the token from the cookie (but the new token will be saved).
 * @internal
 */
export async function getScaToken(ignoreCache?: boolean): Promise<string> {
  if (cachedToken && !ignoreCache) {
    return cachedToken;
  }

  if (cachedPromise && !ignoreCache) {
    return cachedPromise;
  }

  cachedPromise = fetchScaToken();
  const token = await cachedPromise;
  cachedToken = token;
  return token;
}

/** Internal method to actually fetch the SCA token. */
async function fetchScaToken(): Promise<string> {
  log.debug('Retrieving new SCA token');

  // Request init_client
  const url = getInitClientUrl();
  const response = await fetchRetry(url, {
    method: 'POST',
    headers: {
      'Content-Type': contentTypeJson,
    },
  });

  if (!response) {
    throw new Error('Failed to retrieve authentication token');
  }

  // Parse token from response
  const { token }: { token?: string } = await response.json();
  if (!token) {
    throw new Error('Missing toklen in response');
  }

  log.debug('Retrieved SCA token');

  return token;
}

/** Returns the URL for the init_client endpoint. */
function getInitClientUrl() {
  return `${getUnauthenticatedBaseUrl()}/init_client`;
}

/** Clears any internal state for testing purposes. */
export function clearScaTokenCacheForTest(): void {
  cachedToken = undefined;
  cachedPromise = undefined;
}
